import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  FileText,
  Upload,
  Star
} from 'lucide-react';
import { getCaseStudies, addCaseStudy, updateCaseStudy, deleteCaseStudy } from '../../utils/contentManager';

const CaseStudiesManagement = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    industry: '',
    challenge: '',
    solution: '',
    results: [{ metric: '', description: '' }],
    image: '',
    testimonial: {
      text: '',
      author: '',
      role: ''
    }
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadCaseStudies();
  }, []);

  const loadCaseStudies = () => {
    const caseStudiesData = getCaseStudies();
    setCaseStudies(caseStudiesData);
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingCaseStudy(null);
    setFormData({
      title: '',
      industry: '',
      challenge: '',
      solution: '',
      results: [{ metric: '', description: '' }],
      image: '',
      testimonial: {
        text: '',
        author: '',
        role: ''
      }
    });
  };

  const handleEdit = (caseStudy) => {
    setEditingCaseStudy(caseStudy.id);
    setShowAddForm(true);
    setFormData({
      title: caseStudy.title,
      industry: caseStudy.industry,
      challenge: caseStudy.challenge,
      solution: caseStudy.solution,
      results: caseStudy.results || [{ metric: '', description: '' }],
      image: caseStudy.image || '',
      testimonial: caseStudy.testimonial || {
        text: '',
        author: '',
        role: ''
      }
    });
  };

  const handleSave = () => {
    try {
      if (!formData.title.trim() || !formData.industry.trim() || !formData.challenge.trim()) {
        setError('Title, industry, and challenge are required');
        return;
      }

      const caseStudyData = {
        ...formData,
        results: formData.results.filter(r => r.metric.trim() !== '' || r.description.trim() !== '')
      };

      if (editingCaseStudy) {
        const result = updateCaseStudy(editingCaseStudy, caseStudyData);
        if (result) {
          setSuccess('Case study updated successfully');
        } else {
          setError('Failed to update case study');
        }
      } else {
        const result = addCaseStudy(caseStudyData);
        if (result) {
          setSuccess('Case study added successfully');
        } else {
          setError('Failed to add case study');
        }
      }

      loadCaseStudies();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save case study');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this case study?')) {
      try {
        const result = deleteCaseStudy(id);
        if (result) {
          setSuccess('Case study deleted successfully');
          loadCaseStudies();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete case study');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete case study');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCaseStudy(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleResultChange = (index, field, value) => {
    const newResults = [...formData.results];
    newResults[index] = { ...newResults[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      results: newResults
    }));
  };

  const addResult = () => {
    setFormData(prev => ({
      ...prev,
      results: [...prev.results, { metric: '', description: '' }]
    }));
  };

  const removeResult = (index) => {
    if (formData.results.length > 1) {
      const newResults = formData.results.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        results: newResults
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          image: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Case Studies Management</h1>
          <p className="text-gray-600 mt-2">Manage your client success stories</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Case Study</span>
        </motion.button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            {/* Form Content (unchanged) */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Case Studies List */}
      <div className="space-y-6">
        {caseStudies.map((caseStudy) => (
          <motion.div
            key={caseStudy.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Case Study Content (unchanged) */}
          </motion.div>
        ))}
      </div>

      {caseStudies.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FileText className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-gray-500 mb-4">No case studies found</p>
          <button
            onClick={handleAdd}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first case study
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseStudiesManagement;
